import { App } from 'App';
import { Base64 } from 'js-base64';
import { FB_CONFIG_KEYS } from '../firebase/remoteConfig';
import { getFetchHeaders } from 'utils/fetchHeaders';
import { ROUTES } from 'utils/constants/constants_routes';
import { sendDataForTracking } from 'utils/routeTracking';
import { SESSION_TINK_REASON } from 'utils/constants';
import { useCallback, useRef } from 'react';
import { useCustomNavigation } from 'hooks/useCustomNavigation';
import { useLocation, useNavigate } from 'react-router-dom';
import smartlookClient from 'smartlook-client';
import useApplication from 'hooks/useApplication';
import useClient from 'hooks/useClient';
import useTestAB from 'hooks/utilities/useTestAB';

type SendReportsParams = {
  loanApplicationId?: number;
  transactionReportId: string;
  riskInsightsId: string;
  reportGenerationId: string;
};

type ProviderTinkEvent = {
  providerAccessType?: string;
  providerDisplayName?: string;
  providerName?: string;
  providerAuthenticationFlow?: string;
  vivusPropBankSwipe?: string;
};

export type TinkEvent = ProviderTinkEvent & {
  event?: string;
  element?: string;
  screen?: string;
  sessionId?: string;
  risk_insights_report_id?: string;
  transaction_report_id?: string;
  url?: string;
};

export default function useTink() {
  const { navigateToOnlineBankVerification, onlineBankVerifyRoute } =
    useCustomNavigation();
  const apiTinkInquiries = App.useApi('client/tink/inquiries', 'POST');
  const location = useLocation();
  const navigate = useNavigate();
  const { data: application } = useApplication();
  const { client } = useClient();
  const { variant: variantRedirection } = useTestAB({
    testName: FB_CONFIG_KEYS.FIREBASE_TESTAB_TINK_IFRAME,
  }); // 30% will have redirection enabled

  const { variant: variantAsync } = useTestAB({
    testName: FB_CONFIG_KEYS.FIREBASE_TESTAB_TINK_ASYNC,
  }); // 30% will have redirection enabled
  const isTinkRedirectionEnabled = variantRedirection === 'variantB';
  const isTinkAsyncEnabled =
    isTinkRedirectionEnabled && variantAsync === 'variantA'; // redirection is mandatory to have async enabled
  const tinkRedirectionSuccessPathname = ROUTES.FIRST_LOAN_ONLINE_BANK_CHECK;

  const isTinkRedirectionEnabledRef = useRef(isTinkRedirectionEnabled);
  isTinkRedirectionEnabledRef.current = isTinkRedirectionEnabled;

  const isTinkAsyncEnabledRef = useRef(isTinkAsyncEnabled);
  isTinkAsyncEnabledRef.current = isTinkAsyncEnabled;

  const lastTinkEventSent = useRef<TinkEvent>(undefined);
  // useRef to complete tink events data
  const tinkProviderDataEvent = useRef<ProviderTinkEvent>({
    providerAccessType: undefined,
    providerDisplayName: undefined,
    providerName: undefined,
    providerAuthenticationFlow: undefined,
    vivusPropBankSwipe: 'false', // "String" is easier to manage with the other string properties
  });

  // Method to add Provider info from previous tink events if it's missed in the next events
  const addProdiverDataToTinkEvent = (tinkEvent: TinkEvent) => {
    const providerKeys = Object.keys(tinkProviderDataEvent.current);

    // Set to "true" the bankSwipe property
    if (
      tinkEvent.providerName?.length && // provider name from tink defined
      tinkProviderDataEvent.current.providerName?.length && // provider name from providerTink defined
      tinkEvent.providerName !== tinkProviderDataEvent.current.providerName // different providers
    ) {
      tinkProviderDataEvent.current.vivusPropBankSwipe = 'true';
    }

    // Fill tinkProviderDataEvent from tinkEvent if it's possible
    providerKeys.forEach((key) => {
      const typedKey = key as keyof ProviderTinkEvent;
      if (tinkEvent[typedKey]?.length) {
        tinkProviderDataEvent.current[typedKey] = tinkEvent[typedKey];
      }
    });

    // Fill tinkEvent with providerData in case previous tinkEvents had that info
    providerKeys.forEach((key) => {
      const typedKey = key as keyof ProviderTinkEvent;
      if (
        !tinkEvent[typedKey]?.length &&
        tinkProviderDataEvent.current[typedKey]?.length
      ) {
        tinkEvent[typedKey] = tinkProviderDataEvent.current[typedKey];
      }
    });
  };

  const getLink = useCallback(() => {
    const redirectionEnabled = isTinkRedirectionEnabledRef.current;
    const redirectPathname = redirectionEnabled
      ? ROUTES.FIRST_LOAN_ONLINE_BANK_TINK_WAITING
      : tinkRedirectionSuccessPathname;

    const params = {
      client_id: `${import.meta.env.VITE_TINK_CLIENT_ID}`,
      redirect_uri: `${window.location.origin}${redirectPathname}`,
      market: 'ES',
      locale: 'es_ES',
      report_types: 'RISK_INSIGHTS_REPORT,TRANSACTION_REPORT',
      test: `${import.meta.env.VITE_TINK_CLIENT_USE_TEST_BANK === 'true'}`,
      iframe: `${!redirectionEnabled}`,
      async: `${isTinkAsyncEnabledRef.current}`,
      theme: 'LIGHT', // force light mode
    };

    const url = new URL('https://link.tink.com/1.0/reports/create-report');

    for (const [key, value] of Object.entries(params)) {
      url.searchParams.append(key, value);
    }

    return url;
  }, [onlineBankVerifyRoute, tinkRedirectionSuccessPathname]);

  const sendReportIds = useCallback(async (params: SendReportsParams) => {
    await apiTinkInquiries.fetch({
      headers: getFetchHeaders(),
      body: {
        productName: 'vivus.es',
        ...params,
      },
    });
  }, []);

  const handleTinkV2Error = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);

    const errorReason = searchParams.get('error_reason');

    if (errorReason === 'REQUEST_FAILED_CREATE_REPORT') {
      sessionStorage.setItem(SESSION_TINK_REASON, Base64.encode(errorReason));
      navigate(ROUTES.FIRST_LOAN_ONLINE_BANK_ADD_DOCUMENTS);
      return;
    }

    const errorParam = searchParams.get('error');

    if (errorParam === 'USER_CANCELLED') {
      navigateToOnlineBankVerification();
      return;
    }

    if (errorParam && errorParam !== 'USER_CANCELLED') {
      navigate(`${ROUTES.FIRST_LOAN_ONLINE_BANK_ERROR}?${searchParams}`);
      // eslint-disable-next-line no-console
      console.error(
        'Tink error. Search params',
        decodeURIComponent(location.search),
      );
    }
  }, [location.search, navigate, navigateToOnlineBankVerification]);

  const getTinkNameForSmartlook = (tinkEvent: TinkEvent) => {
    if (tinkEvent?.event) return `TINK_EVENT_${tinkEvent.event}`;
    if (tinkEvent?.screen) return `TINK_SCREEN_${tinkEvent.screen}`;
    if (
      tinkEvent?.transaction_report_id ||
      tinkEvent?.risk_insights_report_id
    ) {
      return `TINK_REPORT`;
    }
    if (tinkEvent?.url) return `TINK_THIRD_PARTY_APP`;
    return `TINK_UNKNOWN`;
  };

  const sendSmartlookEvent = (tinkEvent: TinkEvent) => {
    addProdiverDataToTinkEvent(tinkEvent);
    // Compare current tink event with the previous event sent to avoid duplicated events
    const isNotDuplicatedEvent =
      !lastTinkEventSent.current ||
      Boolean(
        (
          Object.keys(lastTinkEventSent.current) as Array<
            keyof typeof tinkEvent
          >
        ).find((key) => lastTinkEventSent.current?.[key] !== tinkEvent?.[key]),
      );

    // Send event if it's not duplicated to Smartlook
    if (isNotDuplicatedEvent) {
      const tinkName = getTinkNameForSmartlook(tinkEvent);
      smartlookClient.track(tinkName, tinkEvent);
      lastTinkEventSent.current = tinkEvent;

      // Send tink events to BE for tracking
      sendDataForTracking({
        typeEvent: 'event',
        details: tinkName,
        client,
        application,
      });
    }
  };

  return {
    getLink,
    sendReportIds,
    handleTinkV2Error,
    isTinkRedirectionEnabled,
    sendSmartlookEvent,
    tinkRedirectionSuccessPathname,
  };
}
